import api from './api';

const deliveryAgentService = {
  async getAll(status = '') {
    try {
      const params = status ? { status } : {};
      const response = await api.get('/delivery-agents', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch agents', data: [] };
    }
  },

  async getStats() {
    try {
      const response = await api.get('/delivery-agents/stats');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch stats', data: null };
    }
  },

  async create(agentData) {
    try {
      const response = await api.post('/delivery-agents', agentData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to create agent' };
    }
  },

  async update(id, updates) {
    try {
      const response = await api.patch(`/delivery-agents/${id}`, updates);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to update agent' };
    }
  },

  // ── Agent login OTP (admin-relayed) ───────────────────────────────────────

  async getLoginOtp(id) {
    try {
      const response = await api.get(`/delivery-agents/${id}/login-otp`);
      return { success: true, data: response.data }; // { otp, expiresAt } | null
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch login OTP', data: null };
    }
  },

  async generateLoginOtp(id) {
    try {
      const response = await api.post(`/delivery-agents/${id}/login-otp`);
      return { success: true, data: response.data }; // { otp, expiresAt, phone }
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to generate login OTP' };
    }
  },

  async markReturned(id) {
    try {
      const response = await api.patch(`/delivery-agents/${id}/mark-returned`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to mark agent as returned' };
    }
  },

  async getAgentAssignments(id) {
    try {
      const response = await api.get(`/delivery-agents/${id}/assignments`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch assignments', data: [] };
    }
  },

  async getOrderAssignment(orderId) {
    try {
      const response = await api.get(`/delivery-agents/order/${orderId}/assignment`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch assignment', data: null };
    }
  },

  async updateAvailability(id, availability) {
    try {
      const response = await api.patch(`/delivery-agents/${id}/availability`, { availability });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to update availability' };
    }
  },

  async remove(id) {
    try {
      const response = await api.delete(`/delivery-agents/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to remove agent' };
    }
  },

  // ── Admin-managed order assignment ────────────────────────────────────────

  async getUnassignedOrders(scheduledLeadHours) {
    try {
      const params = scheduledLeadHours != null ? { scheduledLeadHours } : {};
      const response = await api.get('/delivery-agents/unassigned-orders', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch unassigned orders', data: [] };
    }
  },

  async getAgentLoad(id) {
    try {
      const response = await api.get(`/delivery-agents/${id}/load`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch agent load', data: null };
    }
  },

  async assignOrder(orderId, agentId) {
    try {
      const response = await api.post(`/delivery-agents/orders/${orderId}/assign`, { agentId });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to assign order' };
    }
  },

  async reassignOrder(orderId, newAgentId) {
    try {
      const response = await api.post(`/delivery-agents/orders/${orderId}/reassign`, { newAgentId });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to reassign order' };
    }
  },

  async unassignOrder(orderId, reason) {
    try {
      const response = await api.post(`/delivery-agents/orders/${orderId}/unassign`, reason ? { reason } : {});
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to unassign order' };
    }
  },

  // ── Registration requests (admin approval queue) ──────────────────────────

  async getRegistrationRequests(status = '') {
    try {
      const params = status ? { status } : {};
      const response = await api.get('/delivery-agents/registration-requests', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch requests', data: [] };
    }
  },

  async getPendingRegistrationCount() {
    try {
      const response = await api.get('/delivery-agents/registration-requests/pending-count');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch count', data: { count: 0 } };
    }
  },

  async approveRegistrationRequest(id) {
    try {
      const response = await api.post(`/delivery-agents/registration-requests/${id}/approve`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to approve request' };
    }
  },

  async rejectRegistrationRequest(id, reason = '') {
    try {
      const response = await api.post(`/delivery-agents/registration-requests/${id}/reject`, { reason });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to reject request' };
    }
  },
};

export default deliveryAgentService;